import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { 
  Upload, 
  Loader2, 
  BookOpen, 
  Trash2, 
  Eye, 
  EyeOff,
  FileSpreadsheet,
  CheckCircle2,
  XCircle
} from "lucide-react";

export default function AdminLearning() {
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch all modules (admin view)
  const { data: modules, isLoading, refetch } = trpc.learnAdmin.allPaths.useQuery();

  // Mutations
  const uploadMutation = trpc.learnAdmin.uploadExcel.useMutation({
    onSuccess: (data) => {
      toast.success(
        `Import successful! ${data.modulesCreated} modules, ${data.slidesCreated} slides, ${data.questionsCreated} questions created.`
      );
      setIsUploadDialogOpen(false);
      setSelectedFile(null);
      setIsUploading(false);
      refetch();
    },
    onError: (error) => {
      toast.error(`Upload failed: ${error.message}`);
      setIsUploading(false);
    },
  });

  const deleteMutation = trpc.learnAdmin.deletePath.useMutation({
    onSuccess: () => {
      toast.success("Module deleted successfully");
      refetch();
    },
    onError: (error) => {
      toast.error(`Failed to delete module: ${error.message}`);
    },
  });

  const togglePublishMutation = trpc.learnAdmin.updatePath.useMutation({
    onSuccess: () => {
      toast.success("Module updated successfully");
      refetch();
    },
    onError: (error) => {
      toast.error(`Failed to update module: ${error.message}`);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error("Please select an Excel file (.xlsx or .xls)");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    setIsUploading(true);

    try {
      // Read file as base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target?.result as string;
        // Remove data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64, prefix
        const base64Content = base64.split(',')[1];

        // Upload
        uploadMutation.mutate({ fileContent: base64Content });
      };
      reader.readAsDataURL(selectedFile);
    } catch (error) {
      toast.error("Failed to read file");
      setIsUploading(false);
    }
  };

  const handleDelete = (moduleId: string) => {
    if (confirm("Are you sure you want to delete this module? This will also delete all slides and quiz questions.")) {
      deleteMutation.mutate({ id: moduleId });
    }
  };

  const handleTogglePublish = (moduleId: string, currentStatus: boolean) => {
    togglePublishMutation.mutate({
      id: moduleId,
      is_published: !currentStatus,
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Learning Center Management</h1>
            <p className="text-muted-foreground mt-1">
              Upload training modules and manage learning content
            </p>
          </div>
          <Button onClick={() => setIsUploadDialogOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Upload Excel
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Training Modules</CardTitle>
            <CardDescription>
              {modules?.length || 0} modules in the system
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : modules && modules.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Sector</TableHead>
                    <TableHead>Region</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {modules.map((module: any) => (
                    <TableRow key={module.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <BookOpen className="h-4 w-4 text-muted-foreground" />
                          {module.title}
                        </div>
                      </TableCell>
                      <TableCell>
                        {module.level && (
                          <Badge variant="outline">{module.level}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {module.sector && (
                          <Badge variant="secondary">{module.sector}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {module.region && (
                          <Badge variant="secondary">{module.region}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {module.is_published ? (
                          <Badge variant="default" className="gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            Published
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="gap-1">
                            <XCircle className="h-3 w-3" />
                            Draft
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {module.duration_minutes ? `${module.duration_minutes} min` : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTogglePublish(module.id, module.is_published)}
                          >
                            {module.is_published ? (
                              <>
                                <EyeOff className="h-4 w-4 mr-1" />
                                Unpublish
                              </>
                            ) : (
                              <>
                                <Eye className="h-4 w-4 mr-1" />
                                Publish
                              </>
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(module.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <FileSpreadsheet className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  No modules yet. Upload an Excel file to get started.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upload Dialog */}
        <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Training Modules</DialogTitle>
              <DialogDescription>
                Upload an Excel file containing training modules, slides, and quiz questions.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <FileSpreadsheet className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload">
                  <Button variant="outline" asChild>
                    <span>
                      <Upload className="mr-2 h-4 w-4" />
                      Select Excel File
                    </span>
                  </Button>
                </label>
                {selectedFile && (
                  <p className="text-sm text-muted-foreground mt-4">
                    Selected: {selectedFile.name}
                  </p>
                )}
              </div>

              <div className="bg-muted p-4 rounded-lg text-sm">
                <p className="font-medium mb-2">Expected Excel format:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Training_id, Training_level, sector, region</li>
                  <li>Training_title, objective</li>
                  <li>content_id, content_title, content</li>
                  <li>question_id, question, answer_A/B/C, correct_answer, explanation</li>
                </ul>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsUploadDialogOpen(false);
                  setSelectedFile(null);
                }}
                disabled={isUploading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={!selectedFile || isUploading}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

